import React from 'react';
import { MotiviewKillerQuestion } from 'typings/target_group';
import { NO_OPTION_ID, optionalQuestionCount } from 'utils/killerQuestions';
import styles from './KillerQuestionsSummary.module.css';

interface KillerQuestionsSummaryProps {
    questions: MotiviewKillerQuestion[];
    minCorrect: number;
    redirectUrl: string;
}

/**
 * Read-only view of a group's killer questions.
 *
 * Killer questions are authored in the creation wizard and the backend refuses
 * to change them afterwards, so this exists purely so a recruiter can see what
 * a live group asks. There is deliberately no way to edit from here.
 */
export const KillerQuestionsSummary = ({
    questions,
    minCorrect,
    redirectUrl,
}: KillerQuestionsSummaryProps) => {
    const optionalCount = optionalQuestionCount(questions);

    const yesNoAnswer = (question: MotiviewKillerQuestion): string =>
        question.correct_option_ids[0] === NO_OPTION_ID ? 'No' : 'Yes';

    return (
        <div className={styles.summary}>
            <p className={styles.note}>
                <span className={styles.noteIcon}>🔒</span>
                <span>
                    Killer questions are set when the group is created and
                    cannot be changed. To use different questions, create a new
                    group.
                </span>
            </p>

            <ol className={styles.list}>
                {questions.map((question, index) => (
                    <li key={question.id} className={styles.item}>
                        <span className={styles.number}>{index + 1}</span>

                        <div className={styles.body}>
                            <div className={styles.questionText}>
                                {question.text}
                            </div>

                            {question.type === 'MULTIPLE_CHOICE' ? (
                                // The highlighted chips already say which
                                // options are correct, so no separate line.
                                <ul className={styles.options}>
                                    {(question.options ?? []).map((option) => {
                                        const isCorrect =
                                            question.correct_option_ids.includes(
                                                option.id,
                                            );
                                        return (
                                            <li
                                                key={option.id}
                                                className={
                                                    isCorrect
                                                        ? styles.optionCorrect
                                                        : styles.option
                                                }>
                                                {isCorrect ? '✓ ' : ''}
                                                {option.text}
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <div className={styles.meta}>
                                    <span className={styles.answer}>
                                        ✓ {yesNoAnswer(question)}
                                    </span>
                                </div>
                            )}

                            {question.must_be_correct && (
                                <div className={styles.meta}>
                                    <span className={styles.required}>
                                        Must be correct
                                    </span>
                                </div>
                            )}
                        </div>
                    </li>
                ))}
            </ol>

            <div className={styles.rules}>
                <span className={styles.rulesLabel}>How it is judged</span>
                {optionalCount > 0 ? (
                    <p>
                        Of the {optionalCount} question
                        {optionalCount === 1 ? '' : 's'} not marked as required,
                        at least <strong>{minCorrect}</strong> must be correct.
                    </p>
                ) : (
                    <p>Every question must be answered correctly.</p>
                )}
                {redirectUrl && (
                    <p>
                        Rejected candidates are sent to{' '}
                        <a
                            href={redirectUrl}
                            target="_blank"
                            rel="noreferrer noopener">
                            {redirectUrl}
                        </a>
                    </p>
                )}
            </div>
        </div>
    );
};
